import { BeheerVosStation } from "@/entities/medewerkers-op-station";
import { Button, DataTable, DataTableColumnHeader, Icon } from "@/shared/components";
import type { DataTableFeatures } from "@/shared/components/table/data-table-features";
import { DataTableToolbarOptions } from "@/shared/components/table/data-table-toolbar";
import { capitalize } from "@/shared/utils";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { EditVosStationDialog } from "./edit-station-dialog";

interface VossenTableProps {
    stations: BeheerVosStation[];
}

export function VosStationsTable({ stations }: Readonly<VossenTableProps>) {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedStation, setSelectedStation] = useState<BeheerVosStation | null>(null);

    const columnHelper = createColumnHelper<DataTableFeatures, BeheerVosStation>();
    const columns = columnHelper.columns([
        columnHelper.accessor("stationnaam", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Stationsnaam" />,
            cell: ({ row }) => `${row.original.stationnaam} (${capitalize(row.original.stationcode)})`,
        }),
        columnHelper.accessor("bemenst", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
            cell: ({ row }) => (row.original.bemenst ? "Bemenst" : "Onbemenst"),
        }),
        columnHelper.accessor("beschikbareVossen", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Beschikbare VOS" />,
            cell: ({ row }) => row.original.beschikbareVossen.length,
        }),
        columnHelper.accessor("veilig", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Veilig" />,
            cell: ({ row }) => (
                <Icon
                    className={row.original.veilig ? "text-content-success" : "text-content-error"}
                    name={row.original.veilig ? "check-circle" : "error"}
                />
            ),
        }),
        columnHelper.display({
            id: "Acties",
            header: "Acties",
            cell: ({ row }) => (
                <div className="flex flex-row gap-2">
                    <Button
                        variant="tertiary"
                        iconOnly
                        icon="edit"
                        onClick={() => {
                            setDialogOpen(true);
                            setSelectedStation(row.original);
                        }}
                    />
                </div>
            ),
        }),
    ]);

    const toolbarOptions: DataTableToolbarOptions<BeheerVosStation> = {
        filters: {
            globalFilter: {
                columns: ["stationnaam"],
                placeholder: "Zoek op stationsnaam...",
            },
        },
    };

    const onOpenChange = (open: boolean) => {
        if (!open) setSelectedStation(null);
        setDialogOpen(open);
    };

    return (
        <div>
            <DataTable
                columns={columns}
                data={stations}
                toolbarOptions={toolbarOptions}
                onDoubleClick={(vos) => {
                    setSelectedStation(vos.original);
                    setDialogOpen(true);
                }}
            />
            {selectedStation !== null && (
                <EditVosStationDialog
                    key={`upsert-vos-${selectedStation?.stationcode}`}
                    station={selectedStation}
                    open={dialogOpen}
                    onOpenChange={onOpenChange}
                />
            )}
        </div>
    );
}
